import MomentUtils from '@date-io/moment';
import {
  Box,
  Button,
  FormControl,
  Grid,
  Input,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Canceler } from 'axios';
import moment, { Moment } from 'moment-timezone';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { SubmitControl } from '../../components/SubmitControl';
import useBackdrop from '../../hooks/useBackdrop';
import useSnackbar from '../../hooks/useSnackbar';
import api from '../../services/api/base';
import { getLuckyDraw, saveLuckyDraw } from '../../services/api/LuckyDraw';
import { getSimpleUserSegments } from '../../services/api/Segment';
import {
  ILuckyDraw,
  ILuckyDrawCreator,
  ILuckyDrawGift,
  ISuccessResult,
  IUserSegment,
  LuckyDrawEventType,
  LuckyDrawGiftType,
} from '../../services/types';
import useStyles from './styles';

interface ILuckyDrawViewProps {
  menuType: 'giftbox' | 'event';
  luckyDrawId?: number;
  onCreated?: (data: ILuckyDrawCreator) => void;
  onError?: (error: Error) => void;
  onChangeActivation?: (luckyDrawId: number) => void;
  onClickDeleteButton?: (luckyDrawId: number) => void;
}

const LuckyDrawView = (props: ILuckyDrawViewProps): JSX.Element => {
  const { menuType, luckyDrawId, onCreated, onError } = props;
  const classes = useStyles();
  const prefix = menuType === 'giftbox' ? 'Gift Box ' : '';

  const [title, setTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [startsAt, setStartsAt] = useState<Moment>(moment().startOf('day'));
  const [expiredAt, setExpiredAt] = useState<Moment>(
    moment()
      .add(1, 'day')
      .startOf('day'),
  );
  const [segments, setSegments] = useState<IUserSegment[]>([]);
  const [selectedSegmentId, setSelectedSegmentId] = useState<number>(0);
  const [cooldown, setCoolDown] = useState<number>(menuType === 'giftbox' ? 0 : 12 * 60);
  const [drawCountDown, setDrawCountDown] = useState<number>(menuType === 'giftbox' ? 60 : 30);
  const [liveTimeTo, setLiveTimeTo] = useState<number>(60);
  const [eventType, setEventType] = useState<LuckyDrawEventType>(menuType === 'giftbox' ? LuckyDrawEventType.Giftbox : LuckyDrawEventType.General);
  const [storyId, setStoryId] = useState<number>(0);
  const [participantsNum, setParticipantsNum] = useState<number>(2000);

  const [validationMessage, setValidationMessage] = useState<string>('');

  const [giftFormType, setGiftFormType] = useState<LuckyDrawGiftType>(LuckyDrawGiftType.Coin);
  const [giftFormAmount, setGiftFormAmount] = useState<number>(0);
  const [giftFormWinnerNum, setGiftFormWinnerNum] = useState<number>(0);
  const [giftFormTargetId, setGiftFormTargetId] = useState<number>(0);
  const [giftFormRedirectTo, setGiftFormRedirectTo] = useState<string>('');
  const [validForDays, setValidForDays] = useState<number>(0);

  const [luckyDrawGifts, setLuckyDrawGifts] = useState<ILuckyDrawGift[]>([]);

  const [currentLuckyDrawId, setCurrentLuckyDrawId] = useState<number>();
  const [pendingInitialLoading, setPendingInitialLoading] = useState<boolean>(false);

  const [giftNum, setGiftNum] = useState<number>(0);

  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const luckyDrawCanceler = useRef<Canceler>(() => undefined);
  const segmentsCanceler = useRef<Canceler>(() => undefined);

  const originalData = useRef<Partial<ILuckyDrawCreator>>({
    title: '',
    description: '',
    eventType: menuType === 'giftbox' ? LuckyDrawEventType.Giftbox : LuckyDrawEventType.General,
    chatroomStoryId: 0,
    startsAt: new Date(),
    expiredAt: new Date(),
    countdownMinutes: 30,
    segmentId: 0,
    cooldownMinutes: 0,
    participantsNum: 0,
  });

  const fetchUserSegments = async () => {
    segmentsCanceler.current();
    try {
      const [fetchPromise, cancel] = getSimpleUserSegments<IUserSegment>(true);
      segmentsCanceler.current = cancel;
      const result = await fetchPromise;

      if (result && result.status === 200 && result.data) {
        const { items } = result.data;
        setSegments(items);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  useEffect(() => {
    fetchUserSegments();

    return () => segmentsCanceler.current();
  }, []);

  useEffect(() => {
    if (!luckyDrawId) {
      return;
    }

    const fetchData = async () => {
      luckyDrawCanceler.current();

      try {
        setPendingInitialLoading(true);
        const [fetchPromise, cancel] = getLuckyDraw(luckyDrawId);
        luckyDrawCanceler.current = cancel;
        const result = await fetchPromise;

        if (result.status === 200) {
          const data = result.data;
          originalData.current = data;
          setStatesFromData(data);
        }
      } catch (e) {
        if (api.isCancel(e)) return;

        console.error('e', e);

        if (onError) {
          onError(e);
        }
      } finally {
        setPendingInitialLoading(false);
      }
    };

    fetchData();

    return () => luckyDrawCanceler.current();
  }, [onError, luckyDrawId]);

  useEffect(() => {
    setGiftNum(luckyDrawGifts.length);

    let sum = giftFormWinnerNum;
    for (const gift of luckyDrawGifts) {
      sum += gift.winnerNum;
    }

    if (luckyDrawGifts.length === 5) {
      setGiftFormWinnerNum(participantsNum - sum);
    }

    if (participantsNum < sum) {
      setValidationMessage(`Sum of winner is greater than estimated participants.`);
    } else {
      setValidationMessage('');
    }
  }, [luckyDrawGifts, participantsNum]);

  useEffect(() => {
    if (liveTimeTo <= cooldown) {
      setLiveTimeTo(cooldown + 60);
    }
  }, [cooldown]);

  const updateGiftFormRedirectTo = useCallback(() => {
    let defaultRedirectUri = '';

    switch (giftFormType) {
      case LuckyDrawGiftType.Coin:
        defaultRedirectUri = 'radish://coin-details';
        break;
      case LuckyDrawGiftType.Sale:
        defaultRedirectUri = 'radish://store';
        break;
      case LuckyDrawGiftType.Coupon:
        defaultRedirectUri = `radish://stories/${giftFormTargetId ? giftFormTargetId : ''}`;
        break;
      case LuckyDrawGiftType.Lose:
        break;
    }

    setGiftFormRedirectTo(defaultRedirectUri);
  }, [giftFormType, giftFormTargetId]);

  useEffect(() => {
    updateGiftFormRedirectTo();
  }, [updateGiftFormRedirectTo, giftFormType, giftFormTargetId]);

  const setStatesFromData = (data: Partial<ILuckyDrawCreator>) => {
    data.title !== undefined && setTitle(data?.title);
    data.description !== undefined && setDescription(data?.description);
    data.segmentId !== undefined && setSelectedSegmentId(data?.segmentId);
    data.participantsNum !== undefined && setParticipantsNum(data?.participantsNum);
    data.countdownMinutes !== undefined && setDrawCountDown(data?.countdownMinutes);
    data.cooldownMinutes !== undefined && setCoolDown(data?.cooldownMinutes);
    data.countdownMinutes !== undefined &&
      data.cooldownMinutes !== undefined &&
      setLiveTimeTo(data?.cooldownMinutes + data?.countdownMinutes) &&
      data?.countdownMinutes;
    data.startsAt !== undefined && setStartsAt(moment.utc(data?.startsAt));
    data.expiredAt !== undefined && setExpiredAt(moment.utc(data?.expiredAt));
    data.eventType !== undefined && setEventType(data?.eventType);
    data.chatroomStoryId !== undefined && setStoryId(data?.chatroomStoryId);

    data.gifts !== undefined && setLuckyDrawGifts(data.gifts);
    setCurrentLuckyDrawId(data.id);
  };

  const stateToPayload = (): Partial<ILuckyDrawCreator> => {
    const payload: Partial<ILuckyDrawCreator> = {
      title,
      description,
      startsAt: startsAt?.toDate(),
      segmentId: selectedSegmentId,
      chatroomStoryId: storyId,
      eventType,
      participantsNum,
      cooldownMinutes: cooldown,
      countdownMinutes: menuType === 'giftbox' ? liveTimeTo - cooldown : drawCountDown,
      gifts: luckyDrawGifts,
    };
    if (menuType !== 'giftbox') {
      payload.expiredAt = expiredAt?.toDate();
    }
    return payload;
  };

  const handleClickSubmit = async () => {
    if (luckyDrawGifts.length !== 6) {
      snackbar.error(`Gift number must be 6`);
      return;
    }

    const payload: Partial<ILuckyDrawCreator> = stateToPayload();

    try {
      backdrop.show();

      const result = await saveLuckyDraw(payload);
      if (api.isResponseSuccess(result)) {
        if (result.data.success === false) {
          snackbar.error(
            result.data.errorCode === 'DUPLICATED_DATE' ? 'Lucky Draw is already scheduled for the time you have selected.' : result.data.message,
          );
        } else if (originalData.current) {
          setStatesFromData(originalData.current);
          setLuckyDrawGifts([]);
          if (onCreated) {
            onCreated((result.data as ISuccessResult<ILuckyDraw>).data);
          }
        }
      }
    } catch (e) {
      console.error(e);
      if (onError) {
        onError(e);
      }
    } finally {
      backdrop.hide();
    }
  };

  const isReadOnly = !!currentLuckyDrawId;

  const addGift = () => {
    if (giftNum >= 6) {
      snackbar.error(`Cannot register gifts more than 6`);
      return;
    }

    if (giftFormWinnerNum === undefined || giftFormWinnerNum === 0) {
      snackbar.error(`Gift winner number must be greater than 0.`);
      return;
    }

    if (giftFormType === LuckyDrawGiftType.Coin && !validForDays) {
      snackbar.error(`Validity period  must be greater than 0.`);
      return;
    }

    if (
      [LuckyDrawGiftType.Coin, LuckyDrawGiftType.Coupon, LuckyDrawGiftType.Sale].indexOf(giftFormType) !== -1 &&
      (giftFormAmount === undefined || giftFormAmount === 0)
    ) {
      snackbar.error(`Gift amount must be greater than 0.`);
      return;
    }

    if (
      [LuckyDrawGiftType.Coupon, LuckyDrawGiftType.Sale].indexOf(giftFormType) !== -1 &&
      (giftFormTargetId === undefined || giftFormTargetId === 0)
    ) {
      snackbar.error(`${giftFormType === LuckyDrawGiftType.Coupon ? 'Story Id' : 'Segment Id'}  must be exists.`);
      return;
    }

    switch (giftFormType) {
      case LuckyDrawGiftType.Coin:
        setLuckyDrawGifts([
          ...luckyDrawGifts,
          {
            type: LuckyDrawGiftType.Coin,
            amount: giftFormAmount || 0,
            winnerNum: giftFormWinnerNum || 0,
            redirectTo: giftFormRedirectTo || '',
            validForDays: validForDays || 0,
          },
        ]);
        break;

      case LuckyDrawGiftType.Coupon:
        setLuckyDrawGifts([
          ...luckyDrawGifts,
          {
            type: LuckyDrawGiftType.Coupon,
            amount: giftFormAmount || 0,
            winnerNum: giftFormWinnerNum || 0,
            redirectTo: giftFormRedirectTo || '',
            targetId: giftFormTargetId || 0,
          },
        ]);
        break;

      case LuckyDrawGiftType.Sale:
        setLuckyDrawGifts([
          ...luckyDrawGifts,
          {
            type: LuckyDrawGiftType.Sale,
            amount: giftFormAmount || 0,
            winnerNum: giftFormWinnerNum || 0,
            redirectTo: giftFormRedirectTo || '',
            targetId: giftFormTargetId || 0,
          },
        ]);
        break;

      case LuckyDrawGiftType.Lose:
        setLuckyDrawGifts([
          ...luckyDrawGifts,
          {
            type: LuckyDrawGiftType.Lose,
            winnerNum: giftFormWinnerNum || 0,
            redirectTo: giftFormRedirectTo || '',
          },
        ]);
        break;
    }

    setGiftFormAmount(0);
    setGiftFormTargetId(0);
    setGiftFormWinnerNum(0);
    setValidForDays(0);
    updateGiftFormRedirectTo();
  };

  if (pendingInitialLoading) {
    return <div />;
  }

  return (
    <div className={classes.createLuckyDrawWrap}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        style={{ width: '100%', marginTop: '10px', marginBottom: '16px', paddingLeft: '4px', paddingRight: '22px' }}
      >
        <Typography variant="h6" component="h3" style={{ flex: 1 }}>
          {!currentLuckyDrawId ? (
            `Create ${prefix}Lucky Draw`
          ) : (
            <div>
              {prefix}LuckyDraw : {originalData.current?.id}
            </div>
          )}
        </Typography>
        {!currentLuckyDrawId && (
          <SubmitControl
            mode={'create'}
            onSubmitClick={() => {
              handleClickSubmit();
            }}
          />
        )}
        {currentLuckyDrawId && (
          <Box>
            <Button
              variant="contained"
              color="primary"
              disabled={!moment(expiredAt).isAfter(moment())}
              onClick={() => {
                if (props.onChangeActivation) {
                  props.onChangeActivation(currentLuckyDrawId);
                }
              }}
              style={{ marginRight: '10px' }}
            >
              STOP
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={moment(expiredAt).isAfter(moment())}
              onClick={() => {
                if (props.onClickDeleteButton) {
                  props.onClickDeleteButton(currentLuckyDrawId);
                }
              }}
            >
              DELETE
            </Button>
          </Box>
        )}
      </Box>

      <Grid container direction="row" justify="flex-start">
        <Grid item xs={4} className={classes.verticalGrid}>
          <Paper className={classes.contentSettingWrap}>
            <Typography variant="h6" component="h3" className={classes.sectionTitle}>
              Content Setting
            </Typography>

            <Grid container direction="column" justify="flex-start" alignItems="stretch">
              <Box paddingY={2} width="100%">
                {currentLuckyDrawId && (
                  <Grid item xs={12}>
                    <TextField className={classes.infoTextField} value={currentLuckyDrawId} disabled helperText={'Lucky Draw Id'} />
                  </Grid>
                )}

                <Grid container>
                  <Grid item xs={12}>
                    <TextField
                      label="Title"
                      type="text"
                      className={classes.infoTextField}
                      value={title}
                      onChange={e => setTitle(e.target.value)}
                      disabled={isReadOnly}
                      InputLabelProps={{
                        shrink: true,
                        classes: {
                          root: classes.inputLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          input: classes.tfInput,
                        },
                      }}
                    />
                  </Grid>
                </Grid>

                <Grid container>
                  <Grid item xs={12}>
                    <TextField
                      label="Description"
                      type="text"
                      className={classes.infoTextField}
                      value={description}
                      disabled={isReadOnly}
                      onChange={e => setDescription(e.target.value)}
                      InputLabelProps={{
                        shrink: true,
                        classes: {
                          root: classes.inputLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          input: classes.tfInput,
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                {menuType === 'giftbox' && (
                  <React.Fragment>
                    <Grid container alignItems="stretch">
                      <Grid item xs={12} alignContent="space-between">
                        <Typography variant="subtitle1" className={classes.sectionTitle}>
                          Live Time
                        </Typography>
                      </Grid>

                      <Grid item xs={6} alignContent="space-between">
                        <FormControl>
                          <InputLabel shrink id="demo-simple-select-placeholder-label-label">
                            From
                          </InputLabel>
                          <Select
                            displayEmpty
                            fullWidth
                            value={cooldown}
                            disabled={isReadOnly}
                            onChange={e => setCoolDown(e.target.value as number)}
                            className={classes.smallSelect}
                          >
                            {new Array(24).fill(0).map((v, idx) => (
                              <MenuItem value={idx * 60} key={idx}>
                                {idx === 0 ? 12 : idx} {idx < 12 ? 'AM' : 'PM'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} alignContent="space-between">
                        <FormControl>
                          <InputLabel shrink id="demo-simple-select-placeholder-label-label">
                            To
                          </InputLabel>
                          <Select
                            displayEmpty
                            fullWidth
                            value={liveTimeTo}
                            disabled={isReadOnly}
                            onChange={e => setLiveTimeTo(e.target.value as number)}
                            className={classes.smallSelect}
                          >
                            {new Array(24).fill(0).map((v, idx) => (
                              <MenuItem value={(idx + 1) * 60} disabled={idx + 1 <= cooldown / 60} key={idx}>
                                {idx + 1} {idx + 1 < 12 ? 'AM' : 'PM'}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>

                    <Grid container alignItems="stretch">
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Event starts at
                      </Typography>

                      <Grid item xs={12}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          <KeyboardDateTimePicker
                            format="llll"
                            required
                            margin="normal"
                            id="date-picker-inline"
                            disabled={isReadOnly}
                            value={startsAt.format()}
                            onChange={data => {
                              if (data) setStartsAt(data);
                            }}
                            className={classes.radioButtonGroups}
                          />
                        </MuiPickersUtilsProvider>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Estimated # of participants"
                        className={classes.infoTextField}
                        value={participantsNum}
                        required
                        disabled={isReadOnly}
                        onChange={e => {
                          if (isReadOnly) return;

                          setParticipantsNum(Number(e.target.value));
                        }}
                        InputLabelProps={{
                          shrink: true,
                          classes: {
                            root: classes.inputLabel,
                          },
                        }}
                        InputProps={{
                          classes: {
                            input: classes.tfInput,
                          },
                        }}
                      />
                    </Grid>
                  </React.Fragment>
                )}
                {menuType !== 'giftbox' && (
                  <React.Fragment>
                    <Grid container alignItems="stretch">
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Event expires at
                      </Typography>

                      <Grid item xs={12}>
                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          <KeyboardDateTimePicker
                            format="llll"
                            required
                            margin="normal"
                            id="date-picker-inline"
                            disabled={isReadOnly}
                            value={expiredAt.format()}
                            onChange={data => {
                              if (data) setExpiredAt(data);
                            }}
                            className={classes.radioButtonGroups}
                          />
                        </MuiPickersUtilsProvider>
                      </Grid>
                    </Grid>

                    <Grid container>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Event Type
                      </Typography>

                      <Grid item xs={12}>
                        <Select
                          displayEmpty
                          fullWidth
                          required
                          value={eventType || ''}
                          disabled={isReadOnly}
                          onChange={e => setEventType(e.target.value as LuckyDrawEventType)}
                          className={classes.radioButtonGroups}
                        >
                          <MenuItem key={`eventType-general`} value={LuckyDrawEventType.General}>
                            {LuckyDrawEventType.General}
                          </MenuItem>
                          <MenuItem key={`eventType-chatroom`} value={LuckyDrawEventType.Chatroom}>
                            {LuckyDrawEventType.Chatroom}
                          </MenuItem>
                        </Select>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Story Id"
                        className={classes.infoTextField}
                        value={storyId}
                        disabled={isReadOnly || eventType === LuckyDrawEventType.General}
                        required
                        onChange={e => {
                          if (isReadOnly) return;

                          setStoryId(Number(e.target.value));
                        }}
                        InputLabelProps={{
                          shrink: true,
                          classes: {
                            root: classes.inputLabel,
                          },
                        }}
                        InputProps={{
                          classes: {
                            input: classes.tfInput,
                          },
                        }}
                      />
                    </Grid>

                    <Grid container>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Segment
                      </Typography>

                      <Grid item xs={12}>
                        <Select
                          displayEmpty
                          fullWidth
                          required
                          value={selectedSegmentId || ''}
                          disabled={isReadOnly || segments.length === 0 || eventType === LuckyDrawEventType.Chatroom}
                          onChange={e => setSelectedSegmentId(e.target.value as number)}
                          className={classes.radioButtonGroups}
                        >
                          {segments.map((sg: IUserSegment) => (
                            <MenuItem key={sg.id} value={sg.id}>
                              {sg.id} - {sg.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Estimated # of participants"
                        className={classes.infoTextField}
                        value={participantsNum}
                        required
                        disabled={isReadOnly}
                        onChange={e => {
                          if (isReadOnly) return;

                          setParticipantsNum(Number(e.target.value));
                        }}
                        InputLabelProps={{
                          shrink: true,
                          classes: {
                            root: classes.inputLabel,
                          },
                        }}
                        InputProps={{
                          classes: {
                            input: classes.tfInput,
                          },
                        }}
                      />
                    </Grid>

                    <Grid container>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Draw countdown
                      </Typography>

                      <Grid item xs={12}>
                        <Select
                          displayEmpty
                          fullWidth
                          value={drawCountDown || ''}
                          disabled={isReadOnly}
                          onChange={e => setDrawCountDown(e.target.value as number)}
                          className={classes.radioButtonGroups}
                        >
                          <MenuItem value={5}> 5 minutes </MenuItem>
                          <MenuItem value={10}> 10 minutes </MenuItem>
                          <MenuItem value={30}> 30 minutes </MenuItem>
                          <MenuItem value={360}> 6 hours </MenuItem>
                          <MenuItem value={720}> 12 hours </MenuItem>
                          <MenuItem value={1440}> 24 hours </MenuItem>
                        </Select>
                      </Grid>
                    </Grid>

                    <Grid container>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Cooldown period
                      </Typography>

                      <Grid item xs={12}>
                        <Select
                          displayEmpty
                          fullWidth
                          value={cooldown || ''}
                          disabled={isReadOnly}
                          onChange={e => setCoolDown(e.target.value as number)}
                          className={classes.radioButtonGroups}
                        >
                          <MenuItem value={12 * 60}> 12 hours (0.5 day)</MenuItem>
                          <MenuItem value={24 * 60}> 24 hours (1 day)</MenuItem>
                          <MenuItem value={48 * 60}> 48 hours (2 days)</MenuItem>
                          <MenuItem value={72 * 60}> 72 hours (3 days)</MenuItem>
                          <MenuItem value={168 * 60}> 168 hours (7 days)</MenuItem>
                        </Select>
                      </Grid>
                    </Grid>
                  </React.Fragment>
                )}
              </Box>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={8} className={classes.verticalGrid}>
          <Paper className={classes.contentSettingWrap}>
            <Box>
              <Typography variant="h6" component="h3" className={classes.flexSectionTitle}>
                Lucky Draw Gifts ( {giftNum} / 6 )
              </Typography>

              <Grid container direction="column" justify="flex-start" alignItems="stretch">
                <Grid item xs={12}>
                  <FormControl className={classes.formControl}>
                    <InputLabel shrink>Type</InputLabel>
                    <Select
                      value={giftFormType}
                      onChange={e => setGiftFormType(e.target.value as LuckyDrawGiftType)}
                      displayEmpty
                      className={classes.selectEmpty}
                      disabled={isReadOnly}
                    >
                      <MenuItem value={LuckyDrawGiftType.Coin}>COIN</MenuItem>
                      <MenuItem value={LuckyDrawGiftType.Coupon}>COUPON</MenuItem>
                      <MenuItem value={LuckyDrawGiftType.Sale}>SALE</MenuItem>
                      <MenuItem value={LuckyDrawGiftType.Lose}>LOSE</MenuItem>
                    </Select>
                  </FormControl>
                  {!isReadOnly && (
                    <React.Fragment>
                      {(giftFormType === LuckyDrawGiftType.Coin ||
                        giftFormType === LuckyDrawGiftType.Coupon ||
                        giftFormType === LuckyDrawGiftType.Sale) && (
                        <FormControl className={classes.formControl}>
                          <InputLabel shrink>
                            # of{' '}
                            {giftFormType === LuckyDrawGiftType.Coin
                              ? 'coins'
                              : giftFormType === LuckyDrawGiftType.Sale
                              ? 'Discount Rate'
                              : 'coupons'}
                          </InputLabel>
                          <Input
                            value={giftFormAmount ? giftFormAmount : ''}
                            onChange={e => setGiftFormAmount(Number(e.target.value))}
                            className={classes.selectEmpty}
                          />
                        </FormControl>
                      )}

                      {giftFormType === LuckyDrawGiftType.Coin && (
                        <FormControl className={classes.formControl}>
                          <InputLabel shrink style={{ whiteSpace: 'nowrap' }}>
                            Validity Period (Days)
                          </InputLabel>
                          <Input
                            value={validForDays ? validForDays : ''}
                            onChange={e => setValidForDays(Number(e.target.value))}
                            className={classes.selectEmpty}
                          />
                        </FormControl>
                      )}

                      {(giftFormType === LuckyDrawGiftType.Coupon || giftFormType === LuckyDrawGiftType.Sale) && (
                        <FormControl className={classes.formControl}>
                          <InputLabel shrink>{giftFormType === LuckyDrawGiftType.Coupon ? 'Story ID' : 'Sale Segment ID'}</InputLabel>
                          <Input
                            value={giftFormTargetId ? giftFormTargetId : ''}
                            onChange={e => setGiftFormTargetId(Number(e.target.value))}
                            className={classes.selectEmpty}
                          />
                        </FormControl>
                      )}

                      <FormControl className={classes.formControl}>
                        <InputLabel shrink> # of winners </InputLabel>
                        <Input
                          type="number"
                          value={giftFormWinnerNum ? giftFormWinnerNum : ''}
                          onChange={e => setGiftFormWinnerNum(Number(e.target.value))}
                          className={classes.selectEmpty}
                        />
                      </FormControl>
                    </React.Fragment>
                  )}

                  <FormControl className={classes.formControlForUrl}>
                    <InputLabel shrink> Redirect to </InputLabel>
                    <Input
                      type="text"
                      value={giftFormRedirectTo}
                      onChange={e => setGiftFormRedirectTo(e.target.value)}
                      className={classes.selectEmpty}
                      disabled={isReadOnly}
                    />
                  </FormControl>
                </Grid>

                {!isReadOnly && (
                  <Grid container justify="flex-end" alignItems="center">
                    {validationMessage && <span className={classes.validationMessageLabel}>{validationMessage}</span>}
                    <Button variant="contained" color="primary" disabled={isReadOnly} onClick={addGift}>
                      ADD
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Box>
            <Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">Type</TableCell>
                    <TableCell align="center">Amount</TableCell>
                    <TableCell align="center">Target ID</TableCell>
                    <TableCell align="center">Validity Period</TableCell>
                    <TableCell align="center"># of Winners</TableCell>
                    <TableCell align="center">Redirect To</TableCell>
                    <TableCell align="center">{validationMessage}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {luckyDrawGifts &&
                    luckyDrawGifts.map((g, idx) => {
                      return (
                        <TableRow key={idx}>
                          <TableCell align="center">{g.type}</TableCell>
                          <TableCell align="center">{g.amount}</TableCell>
                          <TableCell align="center">{g.targetId}</TableCell>
                          <TableCell align="center">{g.validForDays}</TableCell>
                          <TableCell align="center">{g.winnerNum}</TableCell>
                          <TableCell align="center">{g.redirectTo}</TableCell>
                          <TableCell
                            align="center"
                            onClick={() => {
                              const newLuckyDrawGift = [...luckyDrawGifts];
                              newLuckyDrawGift.splice(idx, 1);
                              setLuckyDrawGifts(newLuckyDrawGift);
                            }}
                          >
                            {!isReadOnly && <span>X</span>}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </div>
  );
};

export default LuckyDrawView;
